import type { ValidatedRow } from 'v5/platform/row-level-validation';

export const REPORT_ENTITY_TYPES = [
  'contact',
  'callout',
  'shortlist',
  'callsheet',
  'production',
  'booking'
] as const;

export type ReportEntityType = (typeof REPORT_ENTITY_TYPES)[number];

export type ReportTemplateField = { key: string; name: string | null };

/** Stored as-is; keys this client doesn't know survive a read → write. */
export type StoredReportDateFilter = {
  date_field?: string;
  is_relative?: boolean;
  relative?: string | null;
  start?: string | null;
  end?: string | null;
  [key: string]: unknown;
};

/** `filters` keep the v1 search shape untouched: no renaming, no normalisation. */
export type StoredReportQuery = {
  filters: Record<string, unknown>;
  date_filters: StoredReportDateFilter[];
  [key: string]: unknown;
};

export type ReportTemplate = {
  id: number;
  name: string;
  description: string | null;
  entityType: ReportEntityType;
  fields: ReportTemplateField[];
  query: StoredReportQuery;
  archived: boolean;
  userId: string | null;
  createdAt: Date | null;
};

export type ReportTemplateDraft = Pick<
  ReportTemplate,
  'name' | 'description' | 'entityType' | 'fields' | 'query'
>;

export type ReportExportOptions = {
  timezone: string;
  /** `null` exports the whole organisation. */
  enterpriseWorkspaceId: number | null;
};

export interface ReportTemplatesApi {
  /** All templates, archived included; a row the client can't read comes back invalid, not thrown. */
  getTemplates: (
    signal?: AbortSignal
  ) => Promise<ValidatedRow<ReportTemplate>[]>;
  createTemplate: (draft: ReportTemplateDraft) => Promise<ReportTemplate>;
  updateTemplate: (
    id: ReportTemplate['id'],
    draft: ReportTemplateDraft
  ) => Promise<ReportTemplate>;
  setTemplateArchived: (
    id: ReportTemplate['id'],
    archived: boolean
  ) => Promise<ReportTemplate>;
  exportTemplate: (
    id: ReportTemplate['id'],
    options: ReportExportOptions
  ) => Promise<{ accessUrl: string }>;
}
